import { stickerUrl } from "@listou/shared/stickers";

import type { BlogPost } from "../../lib/blog";
import { INLINE_CTA } from "../../lib/blog-markers";
import { no, partial, unstated, yes } from "../comparatifs/cells";

export const meilleureListeDeNoel: BlogPost = {
  h1: "Les meilleures listes de Noël en ligne pour Noël 2026",
  seoTitle: "Meilleure liste de Noël en ligne 2026 : le comparatif pour la famille · Listou",
  seoDescription:
    "Listou, Mes Envies, Milirose, Kadolog, Envy, Mes Souhaits, Oh My Wish, Tilune, Giftster, Elfster : listes de toute la famille, surprise, enfants, Secret Santa et cagnotte. Le comparatif sourcé.",
  publishedOn: "2026-09-29",
  updatedOn: "2026-09-29",
  checkedOn: "2026-09-29",
  summary:
    "À Noël, une liste ne vit jamais seule : chacun a la sienne, les enfants aussi, et tout le monde réserve sur celles des autres. Les services se distinguent donc sur trois points : réunir les listes de la famille derrière un seul lien, garder la surprise à chacun sur sa propre liste, et proposer un Secret Santa. Listou, notre service, fait les trois, gratuitement et sans compte pour les proches, avec des listes qui se décorent aux couleurs de Noël.",
  table: {
    caption: "Dix listes de Noël en ligne, critère par critère",
    columns: [
      "Prix",
      "Compte pour réserver",
      "Surprise",
      "Listes de toute la famille",
      "Secret Santa",
      "Argent",
    ],
    rows: [
      {
        name: "Listou",
        cells: [
          yes("Gratuit"),
          yes("Aucun"),
          yes("Chacun voit les autres listes, jamais la sienne"),
          yes("Un groupe, un lien : chacun choisit son prénom"),
          yes("Intégré au groupe, avec exclusions"),
          partial("Lien vers votre cagnotte"),
        ],
      },
      {
        name: "Mes Envies",
        cells: [
          yes("Gratuit"),
          yes("Aucun"),
          no("Le créateur suit les réservations"),
          unstated("Groupe de listes non décrit"),
          yes("Oui, sans compte"),
          yes("Virement ou cagnotte, sans frais"),
        ],
      },
      {
        name: "Milirose",
        cells: [
          yes("Gratuit"),
          yes("Aucun"),
          partial("Trois niveaux, « Sans surprise » par défaut"),
          partial("Une seule liste, avec une catégorie par prénom"),
          unstated("Non décrit"),
          yes("RIB ou PayPal, sans commission"),
        ],
      },
      {
        name: "Kadolog",
        cells: [
          partial("Gratuit jusqu'à 10 réservations, Premium 29 €"),
          unstated("Non décrit"),
          no("Le créateur suit les cadeaux reçus"),
          unstated("Groupe de listes non décrit"),
          yes("Oui, tirage en ligne"),
          yes("Premium : virement, ou carte à 2,4 %"),
        ],
      },
      {
        name: "Envy",
        cells: [
          yes("Gratuit, sans publicité"),
          yes("Aucun"),
          yes("Pris mais pas par qui, ou tout masqué"),
          partial("Une liste par personne, ou une liste commune"),
          yes("Oui"),
          partial("Lien de cagnotte externe"),
        ],
      },
      {
        name: "Mes Souhaits",
        cells: [
          yes("Gratuit pour les particuliers"),
          no("Compte gratuit obligatoire"),
          yes("Réservation cachée au propriétaire"),
          unstated("Groupe de listes non décrit"),
          yes("Oui, avec exclusions"),
          unstated("Non décrit pour les particuliers"),
        ],
      },
      {
        name: "Oh My Wish",
        cells: [
          yes("Gratuit"),
          unstated("Non précisé"),
          yes("Ni quoi, ni par qui"),
          partial("Plusieurs listes, suivies dans l'appli"),
          yes("Oui"),
          partial("Cagnotte, frais non décrits"),
        ],
      },
      {
        name: "Tilune",
        cells: [
          yes("Gratuit"),
          unstated("Non précisé"),
          yes("Cachée par défaut, réglable"),
          yes("Un lien pour les listes de la famille"),
          yes("Oui, avec exclusions"),
          partial("Cagnotte : 3,5 % des sommes collectées"),
        ],
      },
      {
        name: "Giftster",
        cells: [
          yes("Gratuit"),
          partial("Aucun sur une liste publique, depuis le site"),
          yes("Oui, sauf listes d'enfants gérées"),
          yes("Groupe familial, comptes enfants"),
          yes("Oui, avec exclusions"),
          partial("Engagements, sans collecte d'argent"),
        ],
      },
      {
        name: "Elfster",
        cells: [
          yes("Gratuit"),
          no("Avoir tiré la personne, ou la suivre en ami"),
          yes("Achat invisible au propriétaire"),
          partial("Échanges et comptes famille"),
          yes("Oui"),
          unstated("Non décrit"),
        ],
      },
    ],
  },
  body: `
## Comment nous avons comparé

Nous avons retenu dix services visibles en France pour « liste de Noël en ligne » et « liste de Noël famille ». Listou est notre service : nous le présentons avec les autres, et chaque information sur les autres vient de leur propre site, listé en bas de page avec la date de consultation. Quand un site ne décrit pas une fonction, nous l'écrivons « non décrit » plutôt que de supposer qu'elle n'existe pas.

## Ce qui compte pour Noël en famille

- **Toutes les listes au même endroit.** Envoyer six liens dans la conversation familiale, c'est l'assurance que Tonton n'en ouvrira qu'un. Mieux vaut un seul lien, où chacun trouve les listes de tous.
- **La surprise pour chacun.** Tout le monde est à la fois celui qui offre et celui qui reçoit : chacun doit voir ce qui est pris chez les autres, jamais chez lui.
- **Les enfants et Mamie.** Les petits ne tiennent pas leur liste seuls, et tout le monde n'a pas de téléphone.
- **Le Secret Santa.** Beaucoup de familles tirent au sort plutôt que d'offrir à tout le monde.
- **Personne ne s'inscrit.** Chaque compte demandé fait perdre un participant.

## Listou : toute la famille derrière un seul lien

Sur **Listou**, un **groupe** réunit les listes de toute la famille derrière un seul lien, « Noël chez les Martin ». Vous l'envoyez dans la conversation : chacun choisit son prénom, remplit sa liste et réserve sur celles des autres, sans créer de compte. Chacun voit ce qui est réservé sur les listes des autres, jamais sur la sienne : la surprise tient jusqu'au jour J, et les deux tantes n'offrent pas le même pull.

Pour ceux qui ne viendront pas eux-mêmes, l'organisateur remplit leur liste jusqu'à ce qu'ils la réclament. Un parent peut choisir le prénom de son enfant et tenir sa liste.

Le **Secret Santa** s'ajoute au groupe : des gommettes de couleur évitent qu'un couple se tire, le tirage mène à la liste de la personne tirée, et l'année suivante personne ne retombe sur la même personne.

Chaque liste est une liste Listou complète. On ajoute un article de n'importe quelle boutique en collant son lien, et on la décore dans l'onglet Apparence : **6 modèles de page**, **13 palettes**, **4 polices**, et **162 stickers** dessinés, dont un thème Noël (sapin, boule, chaussette, bonnet) pour l'en-tête ou un fond à motifs.

Ses limites : Listou ne fait transiter aucun argent (on ajoute un lien vers une cagnotte externe), et dans un Secret Santa, l'organisateur peut voir le tirage, ce dont les participants sont prévenus.
${INLINE_CTA}## Mes Envies : la lettre au Père Noël

**Mes Envies** est gratuit, accepte n'importe quelle boutique, en ligne ou en magasin, et laisse réserver sans compte. Pour les enfants, on remplit la liste avec eux puis on imprime une lettre au Père Noël personnalisée. Le Secret Santa est gratuit et sans compte, et les participations en argent vont directement sur votre compte, sans frais.

Le créateur suit les cadeaux réservés, sans réglage décrit pour les cacher. Chaque liste se partage par son lien ; un groupe réunissant les listes de plusieurs personnes n'est pas décrit.

## Milirose : une seule liste pour toute la famille

**Milirose** est gratuit, accepte n'importe quel site marchand et laisse réserver sans compte. Pour Noël, sa FAQ suggère de créer une catégorie par prénom, pour n'avoir qu'une seule liste pour toute la famille. La surprise se règle sur trois niveaux, « Sans surprise » par défaut. Les proches peuvent participer par virement ou PayPal, sans commission. La FAQ ne décrit pas de tirage au sort de Secret Santa.

## Kadolog : un Secret Santa et le paiement par carte

**Kadolog** accepte n'importe quelle boutique, physique ou en ligne, et propose un tirage au sort de Secret Santa. Sa formule gratuite s'arrête à 10 réservations : au-delà, Premium coûte 29 € par liste et permet de recevoir le montant des cadeaux par virement sans frais ou par carte à 2,4 %. Le créateur suit les cadeaux reçus. Chacun envoie le lien de sa liste, et de celles de ses enfants ; un groupe réunissant toutes les listes n'est pas décrit.

## Envy : la surprise au choix

**Envy** est gratuit, sans publicité, et les proches réservent sans compte. Par défaut, le propriétaire sait qu'une envie est prise mais pas par qui, et il peut choisir de ne rien voir du tout. Pour la famille, Envy propose une liste par personne ou une liste commune à toute la maison, où l'on propose, vote et commente. Un Secret Santa est inclus. Pour l'argent, on ajoute le lien d'une cagnotte externe.

## Mes Souhaits : la surprise gardée, mais un compte pour réserver

Sur **Mes Souhaits**, gratuit pour les particuliers, une réservation est visible des autres invités mais reste cachée au propriétaire. Un parent gère depuis son compte une liste par enfant, et le Secret Santa gère les exclusions et envoie à chacun le lien de la liste de la personne tirée. Sa limite : il faut créer un compte gratuit pour cliquer sur « Réserver ».

## Oh My Wish : la surprise totale

**Oh My Wish** est gratuit et accepte n'importe quelle boutique. Le bénéficiaire ne sait ni qui a réservé, ni même quel souhait a été choisi. On crée sa liste et celles de ses enfants, une liste peut se gérer avec son conjoint, et l'appli permet de suivre les listes de ses proches. Il propose un Secret Santa et une cagnotte, dont les frais ne sont pas décrits sur la page Noël.

## Tilune : un lien pour la famille, une cagnotte payante

Les listes de Noël de **Tilune** sont gratuites. Par défaut, une réservation est visible des autres invités mais pas du créateur, qui peut changer ce réglage. Chacun a sa liste, et Tilune permet de partager un lien unique regroupant les listes de la famille. Le Secret Santa gère les exclusions. La cagnotte, elle, prélève 3,5 % des sommes collectées.

## Giftster : le groupe familial, en anglais

**Giftster** est gratuit et fonctionne avec des groupes : chaque membre voit les listes des autres et peut y acheter. Les enfants ont de vrais comptes, gérés depuis celui du parent, qui voit alors ce qui est pris sur leurs listes. Un tirage au sort avec exclusions est intégré. Sa limite pour une famille française : l'interface est uniquement en anglais.

## Elfster : pensé pour l'échange de cadeaux

**Elfster** est gratuit et en français. Quand quelqu'un marque un cadeau comme acheté, les autres le voient, mais pas le propriétaire de la liste. Pour marquer un article, il faut toutefois avoir tiré la personne dans un échange ou la suivre en ami, donc avoir un compte. Un compte Family Manager permet de gérer les listes des enfants.

## Quelle liste choisir ?

- **Toute la famille derrière un seul lien, sans inscription** : Listou. Tilune propose aussi un lien familial, et Giftster un groupe, en anglais.
- **La surprise pour chacun** : Listou, Mes Souhaits, Oh My Wish, Tilune ou Envy.
- **Un Secret Santa dans le même outil que les listes** : Listou, Envy, Mes Souhaits, Oh My Wish, Tilune, Giftster ou Elfster.
- **Une liste décorée pour Noël** : Listou, avec ses modèles, palettes et stickers de Noël.
- **Des participations en argent sans frais** : Mes Envies ou Milirose.

Pour vous lancer, les [listes en groupe](/listes-en-groupe) réunissent la famille en un lien, et la [liste de Noël](/liste-de-noel) se crée en deux minutes. Pour un tirage au sort seul, voir aussi notre comparatif des [meilleurs sites de Secret Santa](/blog/meilleur-site-secret-santa).
`,
  faq: [
    [
      "Quelle est la meilleure liste de Noël en ligne en 2026 ?",
      "Pour une famille, celle qui réunit les listes de chacun derrière un seul lien et garde la surprise à chacun sur la sienne. Listou le fait gratuitement, sans compte pour les proches, avec un Secret Santa intégré. Tilune propose aussi un lien familial, Giftster un groupe en anglais. Pour recevoir de l'argent sans frais, Mes Envies ou Milirose.",
    ],
    [
      "Comment faire une liste de Noël commune pour toute la famille ?",
      "Sur Listou, créez un groupe de listes et envoyez son lien : chacun choisit son prénom, remplit sa liste et réserve sur celles des autres. Sur Milirose, on peut aussi n'avoir qu'une liste, avec une catégorie par prénom.",
    ],
    [
      "Comment éviter que quelqu'un voie les cadeaux qu'on lui a réservés ?",
      "Choisissez un service qui cache les réservations au propriétaire de la liste : Listou, Mes Souhaits, Oh My Wish, Tilune, Giftster, Elfster, ou Envy en masquant tout. Sur Mes Envies et Kadolog, le créateur suit les réservations.",
    ],
    [
      "Faut-il un compte pour réserver un cadeau de Noël ?",
      "Non sur Listou, Mes Envies, Milirose et Envy. Mes Souhaits demande un compte gratuit pour réserver ; sur Elfster, il faut avoir tiré la personne ou la suivre en ami.",
    ],
    [
      "Comment faire la liste de Noël d'un enfant ?",
      "Créez une liste à son nom et remplissez-la avec lui. Sur Listou, un parent choisit le prénom de son enfant dans le groupe familial et tient sa liste ; Mes Envies propose d'imprimer une lettre au Père Noël ; Giftster et Elfster ont des comptes enfants gérés par le parent.",
    ],
    [
      "Peut-on faire un Secret Santa avec les listes de Noël ?",
      "Oui sur Listou, où le tirage s'ajoute au groupe et mène à la liste de la personne tirée, et sur Envy, Mes Souhaits, Oh My Wish, Tilune, Giftster et Elfster. Mes Envies et Kadolog proposent un tirage à côté de leurs listes.",
    ],
    [
      "Peut-on décorer sa liste de Noël ?",
      "Sur Listou, oui : 6 modèles de page, 13 palettes, 4 polices et 162 stickers, dont un thème Noël, pour l'en-tête ou un fond à motifs.",
    ],
  ],
  sources: [
    { label: "Mes Envies : liste de Noël", url: "https://www.mesenvies.fr/liste-noel.html" },
    { label: "Mes Envies : comparatif", url: "https://www.mesenvies.fr/comparatif.html" },
    {
      label: "Mes Envies : gérer les réservations et dons libres",
      url: "https://aide.mesenvies.fr/hc/331138012/5/gerer-les-reservations-et-dons-libres",
    },
    { label: "Milirose : foire aux questions", url: "https://www.milirose.com/faq.html" },
    {
      label: "Kadolog : tarifs et conditions",
      url: "https://www.kadolog.com/fr-fr/tarifs-et-conditions",
    },
    {
      label: "Kadolog : liste de Noël et Secret Santa",
      url: "https://www.kadolog.com/fr-fr/liste-de-cadeaux-de-noel",
    },
    {
      label: "Kadolog : FAQ des détenteurs d'une liste",
      url: "https://www.kadolog.com/fr-fr/faq/45203",
    },
    { label: "Envy : liste de Noël", url: "https://envynest.fr/liste-de-noel" },
    { label: "Envy : Secret Santa", url: "https://envynest.fr/secret-santa" },
    { label: "Envy : cadeau commun", url: "https://envynest.fr/cadeau-commun" },
    { label: "Mes Souhaits : liste de Noël", url: "https://messouhaits.fr/liste-de-noel" },
    { label: "Mes Souhaits : FAQ", url: "https://messouhaits.fr/faq" },
    { label: "Oh My Wish : liste de Noël", url: "https://ohmywish.me/liste-de-noel" },
    { label: "Oh My Wish : application mobile", url: "https://ohmywish.me/application-mobile" },
    { label: "Oh My Wish : Secret Santa", url: "https://ohmywish.me/liste-secret-santa" },
    { label: "Tilune : liste de Noël", url: "https://www.tilune.fr/liste-noel" },
    { label: "Tilune : cagnotte", url: "https://www.tilune.fr/cagnotte" },
    {
      label: "Tilune : liste multi-enseignes, comment ça marche",
      url: "https://www.tilune.fr/blog/guides/liste-cadeaux-multi-enseignes-comment-ca-marche",
    },
    {
      label: "Giftster : est-ce payant ?",
      url: "https://help.giftster.com/article/41-does-giftster-cost-anything",
    },
    {
      label: "Giftster : fonctionne-t-il dans tous les pays ?",
      url: "https://help.giftster.com/article/127-will-giftster-work-in-any-country",
    },
    {
      label: "Giftster : utiliser Giftster sans compte",
      url: "https://help.giftster.com/article/42-register-to-use-giftster",
    },
    {
      label: "Giftster : inviter des proches dans son groupe",
      url: "https://help.giftster.com/article/33-start-invite-others-to-my-group",
    },
    {
      label: "Giftster : le créateur voit-il les articles réservés ?",
      url: "https://help.giftster.com/article/72-will-list-maker-see-reserved-purchased",
    },
    {
      label: "Giftster : comptes enfants",
      url: "https://help.giftster.com/article/66-how-child-accounts-work",
    },
    {
      label: "Giftster : tirage au sort",
      url: "https://help.giftster.com/article/8-draw-gift-exchange-manager-questions",
    },
    {
      label: "Giftster : cadeaux en commun",
      url: "https://help.giftster.com/article/119-group-gifting",
    },
    {
      label: "Elfster : guide et FAQ",
      url: "https://www.elfster.com/blog/fr/elfster-how-tos-and-helpful-faqs/",
    },
    {
      label: "Elfster : indiquer qu'on a acheté un article",
      url: "https://help.elfster.com/fr/articles/11517983-comment-indiquer-que-j-ai-achete-un-article-figurant-sur-une-autre-liste-de-souhaits-elfster",
    },
    {
      label: "Elfster : gérer les listes d'un compte Family Manager",
      url: "https://help.elfster.com/fr/articles/11518334-comment-gerer-les-listes-de-souhaits-d-un-compte-elfster-family-manager",
    },
  ],
  occasion: "christmas",
  inlineCta: {
    title: "Toute la famille derrière un seul lien",
    text: "Chacun sa liste, chacun réserve sur celles des autres. Gratuit, sans compte.",
    sticker: stickerUrl("noel", "sapin"),
  },
};
